import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Lightbulb, Rocket } from 'lucide-react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

export const ProcessSection = () => {
  const { t } = useTranslation();
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.2 });

  const steps = t('process.steps', { returnObjects: true });
  const icons = [Eye, Lightbulb, Rocket];

  return (
    <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 bg-black relative overflow-hidden">
      {/* Background tech pattern - Hidden on mobile */}
      <div 
        className="absolute inset-0 opacity-[0.05] hidden sm:block"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1644088379091-d574269d422f)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      ></div>

      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-transparent"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <div
          ref={ref}
          className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-center text-white">
            {t('process.title')}{' '}
            <span className="text-gray-500">
              {t('process.titleGray')}
            </span>
          </h2>

          <p className="text-base sm:text-lg md:text-xl text-gray-500 text-center mb-12 sm:mb-16 md:mb-20 max-w-3xl mx-auto px-4">
            {t('process.subtitle')}
          </p>

          {/* Desktop Timeline */}
          <div className="hidden md:block relative">
            <div className="flex justify-between items-start gap-8">
              {steps.map((step, index) => {
                const Icon = icons[index];
                return (
                  <React.Fragment key={index}>
                    <div
                      className={`flex-1 transform transition-all duration-700 delay-${index * 200} ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                      }`}
                    >
                      <div className="relative">
                        {/* Step number badge */}
                        <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full bg-zinc-900 border-2 border-zinc-800 flex items-center justify-center z-10">
                          <span className="text-3xl font-bold text-white">{`0${index + 1}`}</span>
                        </div>

                        {/* Card */}
                        <div className="pt-16 pb-10 px-10 rounded-none bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 min-h-[380px] flex flex-col">
                          <div className="p-5 rounded-none bg-white/5 w-fit mb-8">
                            <Icon className="h-12 w-12 text-white" />
                          </div>

                          <h3 className="text-2xl font-bold text-white mb-5">{step.title}</h3>
                          <p className="text-gray-400 leading-relaxed flex-1 text-lg">{step.description}</p>
                        </div>
                      </div>
                    </div>

                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div className="flex items-center pt-28">
                        <div className="w-16 h-0.5 bg-zinc-800"></div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden space-y-6 sm:space-y-8">
            {steps.map((step, index) => {
              const Icon = icons[index];
              return (
                <div
                  key={index}
                  className={`transform transition-all duration-700 delay-${index * 200} ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                  }`}
                >
                  <div className="relative">
                    {/* Step number badge - Smaller on mobile */}
                    <div className="absolute -top-4 -left-4 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-zinc-900 border-2 border-zinc-800 flex items-center justify-center z-10">
                      <span className="text-2xl sm:text-3xl font-bold text-white">{`0${index + 1}`}</span>
                    </div>

                    {/* Card */}
                    <div className="pt-14 sm:pt-16 pb-6 sm:pb-10 px-6 sm:px-10 rounded-none bg-zinc-900 border border-zinc-800">
                      <div className="p-3 sm:p-5 rounded-none bg-white/5 w-fit mb-6 sm:mb-8">
                        <Icon className="h-8 w-8 sm:h-12 sm:w-12 text-white" />
                      </div>

                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-5">{step.title}</h3>
                      <p className="text-gray-400 leading-relaxed text-base sm:text-lg">{step.description}</p>
                    </div>

                    {/* Connector line for mobile */}
                    {index < steps.length - 1 && (
                      <div className="flex justify-center py-3 sm:py-4">
                        <div className="w-0.5 h-6 sm:h-8 bg-zinc-800"></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};